import { Component, Input } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AttachmentPosition, FIRST_SECTION_BREAK, MAX_SECTION_BREAK, SectionSourceType, SectionType } from 'enums-lib';
import { ISection } from 'interfaces-lib';
import { SectionComponentBase } from '../section-component-base';

export class MergeConfig {
  required: boolean;
  'merge-parts'?: any[];
  'merge-styling'?: MergeStyling;
  'attachment-placement'?: AttachmentPlacement;

  constructor(
    required: boolean,
    mergeParts?: any[],
    mergeStyling?: MergeStyling,
    attachmentPlacement?: AttachmentPlacement,
  ) {
    this.required = required;
    this['merge-parts'] = mergeParts;
    this['merge-styling'] = mergeStyling;
    this['attachment-placement'] = attachmentPlacement;
  }

  static clone(config: MergeConfig): MergeConfig {
    return new MergeConfig(
      config.required,
      config['merge-parts'],
      config['merge-styling'],
      config['attachment-placement']
    );
  }
}

export class AttachmentPlacement {
  position: AttachmentPosition;
  sectionBreak: number;
}

export class MergeStyling {
  attachmentUploadSource: string;
  attachmentFontFamily: string;
  attachmentFontSize: number;
  attachmentFontSizeType: string;
  attachmentPageSizeType: string;
  attachmentMarginType: string;
}

@Component({
    selector: 'app-merge-section',
    templateUrl: './merge-section.component.html',
    standalone: false
})
export class MergeSectionComponent extends SectionComponentBase {
  @Input() formGroup: UntypedFormGroup;

  @Input() set sectionsFormArray(sectionsFormArray: UntypedFormArray) {
    const sectionMergePartFormGroups = this.getSectionMergePartFormGroups(sectionsFormArray);
    this.syncMergePartsFormArray(sectionMergePartFormGroups);
    this.listenForChanges(sectionMergePartFormGroups);
  }

  private static stylingPart: SectionType = SectionType.Merge;
  private static mergeParts = [
    SectionType.DocxTemplate,
    SectionType.HtmlDocxTemplate,
    SectionType.File
  ];

  constructor() {
    super();
  }

  public static override buildForm(formGroup: UntypedFormGroup, section?: ISection): void {
    formGroup.get('sourceType').setValue(SectionSourceType.Workflow);
    formGroup.get('referenceId').setValue('MERGE_1');
    formGroup.get('type').setValue(SectionType.Merge);

    const config = section?.config
      ? MergeConfig.clone(section.config as MergeConfig)
      : new MergeConfig(true);
    formGroup.get('config').setValue(config);

    const configForm = new FormGroup({
      filename: new FormControl('[(${OUTPUT_FILENAME_1})]' ),
      'merge-parts': new FormArray([]), // requires knowledge of other sections to be built
      'merge-styling': this.buildStylingForm(config),
      'attachment-placement': this.buildPlacementForm(config),
    });
    formGroup.setControl('config', configForm);
  }

  private static buildPlacementForm(config: MergeConfig): FormGroup {
    const placement: AttachmentPlacement = config['attachment-placement'];
    const sectionBreakControl = this.buildSectionBreakControl(placement);

    return new FormGroup({
      position: this.buildPositionControl(placement, sectionBreakControl),
      sectionBreak: sectionBreakControl,
    });
  }

  private static buildPositionControl(placement: AttachmentPlacement, sectionBreakControl: FormControl): FormControl {
    const positionControl = new FormControl(placement?.position ?? AttachmentPosition.End);

    positionControl.valueChanges.subscribe((position: AttachmentPosition) => {
      if (position !== AttachmentPosition.SectionBreak) {
        sectionBreakControl.setValue(FIRST_SECTION_BREAK, { emitEvent: false });
      }
    });

    return positionControl;
  }

  private static buildSectionBreakControl(placement: AttachmentPlacement): FormControl {
    return new FormControl(
      placement?.sectionBreak ?? FIRST_SECTION_BREAK,
      [
        Validators.min(FIRST_SECTION_BREAK),
        Validators.max(MAX_SECTION_BREAK),
        MergeSectionComponent.validateWholeSectionBreak
      ]
    );
  }

  private static validateWholeSectionBreak(control: AbstractControl): ValidationErrors | null {
    if (control.value === null || control.value === '') {
      return null;
    }

    return Number.isInteger(Number(control.value)) ? null : { wholeSectionBreak: true };
  }

  private static buildStylingForm(config: MergeConfig): FormGroup {
    const stylingConfig: MergeStyling = config['merge-styling'];
    return new FormGroup({
      attachmentUploadSource: new FormControl(
        stylingConfig?.attachmentUploadSource ?? ''
      ),
      attachmentFontFamily: new FormControl(
        stylingConfig?.attachmentFontFamily ?? 'original'
      ),
      attachmentFontSize: this.buildFontSizeControl(stylingConfig),
      attachmentFontSizeType: new FormControl(
        stylingConfig?.attachmentFontSizeType ?? 'original'
      ),
      attachmentPageSizeType: new FormControl(stylingConfig?.attachmentPageSizeType ?? 'original'),
      attachmentMarginType: new FormControl(stylingConfig?.attachmentMarginType ?? 'original'),
    });
  }

  private static buildFontSizeControl(stylingConfig: MergeStyling) {
    const fontSizeControl = new FormControl(
      stylingConfig?.attachmentFontSize ?? 12,
      [Validators.max(1638)]
    );

    fontSizeControl.valueChanges.subscribe(value => {
      if (value != null) {
        const rounded = Math.round(value * 2) / 2;
        const capped = Math.min(rounded, 1638);
        fontSizeControl.setValue(capped, { emitEvent: false });
      }
    });

    return fontSizeControl;
  }

  private getSectionMergePartFormGroups(sectionsFormArray: UntypedFormArray): UntypedFormGroup[] {
    return sectionsFormArray.controls.filter((sectionFormGroup: UntypedFormGroup) => {
      const type = sectionFormGroup.get('type').value;
      return [MergeSectionComponent.stylingPart, ...MergeSectionComponent.mergeParts].includes(type);
    }) as UntypedFormGroup[];
  }

  private listenForChanges(sectionMergePartFormGroups: UntypedFormGroup[]): void {
    sectionMergePartFormGroups.forEach((fg: UntypedFormGroup) => {
      fg.valueChanges.subscribe({
        next: (_) => {
          this.syncMergePartsFormArray(sectionMergePartFormGroups);
        },
      });
    });
  }

  private syncMergePartsFormArray(mergeSectionFormGroups: FormGroup[]): void {
    const mergePartsArray = this.formGroup.get('config').get('merge-parts') as FormArray;
    mergePartsArray.clear({ emitEvent: false });

    const mergePartSections = mergeSectionFormGroups
      .filter((section) => MergeSectionComponent.mergeParts.includes(section.get('type').value))
      .sort((a, b) =>
        MergeSectionComponent.mergeParts.indexOf(a.get('type').value) -
        MergeSectionComponent.mergeParts.indexOf(b.get('type').value)
      );

    // create a FormGroup for each section required to be merged (defined in the static mergeParts array)
    mergePartSections.forEach((sectionFormGroup) => {
      const mergePartFormGroup = this.createMergePart(sectionFormGroup);
      mergePartsArray.push(mergePartFormGroup, { emitEvent: false });
    });

    // Sync merge-styling from the first File section
    this.syncMergeStyling(mergePartSections);
  }

  private syncMergeStyling(mergePartSections: FormGroup[]): void {
    const mergeStylingGroup = this.formGroup.get('config').get('merge-styling') as FormGroup;
    const fileSection = mergePartSections.find((section) => section.get('type').value === SectionType.File);

    if (fileSection) {
      // Update merge-styling from File section properties
      const stylingFields = [
        'attachmentUploadSource',
        'attachmentFontFamily',
        'attachmentFontSize',
        'attachmentFontSizeType',
        'attachmentPageSizeType',
        'attachmentMarginType'
      ];

      stylingFields.forEach((field) => {
        const value = fileSection.get(field)?.value;
        if (value !== undefined) {
          mergeStylingGroup.get(field)?.setValue(value, { emitEvent: false });
        }
      });
    } else {
      // Reset to empty/default values when no File section exists
      mergeStylingGroup.reset({
        attachmentUploadSource: '',
        attachmentFontFamily: '',
        attachmentFontSize: null,
        attachmentFontSizeType: '',
        attachmentPageSizeType: '',
        attachmentMarginType: ''
      }, { emitEvent: false });

      this.formGroup.get('config').get('attachment-placement')
        ?.reset({ position: AttachmentPosition.End, sectionBreak: FIRST_SECTION_BREAK }, { emitEvent: false });
    }
  }

  createMergePart(sectionFormGroup: FormGroup): UntypedFormGroup {
    const mergePartFormGroup = new UntypedFormGroup({
      id: new FormControl(sectionFormGroup.get('referenceId').value),
      required: new FormControl(sectionFormGroup.get('required').value),
    });

    const isFileSection = sectionFormGroup.get('type').value === SectionType.File;
    if (isFileSection) {
      mergePartFormGroup.addControl('header-footer', new FormControl('INHERIT'));
      mergePartFormGroup.addControl('restart-numbering', new FormControl(true));
    }

    return mergePartFormGroup;
  }
}
